import { useEffect, useState, type FormEvent } from 'react';
import { Heart, Link2, LogIn, MessageSquare, Pin, PinOff, Send, Trash2 } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { BoardComment, BoardMessage } from '../types';
import { formatTime } from '../utils';
import { Badge, Button, EmptyBlock, LoadingBlock, PageHeader, Panel, Textarea } from '../components/ui';

function BoardAvatar({ name, avatar, className = '' }: { name: string; avatar?: string | null; className?: string }) {
  return <span className={'board-avatar ' + className}>{avatar ? <img className="avatar-image" src={avatar} alt="" /> : name.slice(0, 1).toUpperCase()}</span>;
}
export function BoardPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [messages, setMessages] = useState<BoardMessage[] | null>(null);
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [openComments, setOpenComments] = useState<Set<string>>(new Set());
  const [comments, setComments] = useState<Record<string, BoardComment[] | null>>({});
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [commentBusy, setCommentBusy] = useState<Record<string, boolean>>({});
  const [actionBusy, setActionBusy] = useState<Record<string, boolean>>({});
  const canPostWithoutBinding = Boolean(user?.boundToGame || settings.allowUnboundBoardMessages);
  const canCommentWithoutBinding = Boolean(user?.boundToGame || settings.allowUnboundBoardComments);
  const canLikeWithoutBinding = Boolean(user?.boundToGame || settings.allowUnboundBoardLikes);

  const load = async () => {
    const data = await api<{ messages: BoardMessage[] }>('/api/board/messages?limit=60');
    setMessages(data.messages);
  };

  useEffect(() => {
    void load().catch(() => toast('留言加载失败', 'error'));
    const timer = window.setInterval(() => { void load().catch(() => {}); void refresh().catch(() => {}); }, 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return onNeedAuth();
    if (!canPostWithoutBinding) return toast('请先绑定游戏角色后再留言', 'error');
    const text = content.trim();
    if (!text) return;
    setBusy(true);
    try {
      const result = await api<{ moderation: { status: 'approved' | 'pending'; reason: string } }>('/api/board/messages', { method: 'POST', body: JSON.stringify({ content: text }) });
      setContent('');
      await load();
      toast(result.moderation?.status === 'pending' ? '留言已提交，等待管理员复核' : '留言已发布', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '留言失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const loadComments = async (messageId: string) => {
    setComments(previous => ({ ...previous, [messageId]: null }));
    try {
      const data = await api<{ comments: BoardComment[] }>(`/api/board/messages/${messageId}/comments?limit=100`);
      setComments(previous => ({ ...previous, [messageId]: data.comments }));
    } catch (error) {
      setComments(previous => ({ ...previous, [messageId]: [] }));
      toast(error instanceof ApiError ? error.message : '评论加载失败', 'error');
    }
  };

  const toggleComments = (message: BoardMessage) => {
    const opening = !openComments.has(message.id);
    setOpenComments(previous => {
      const next = new Set(previous);
      if (opening) next.add(message.id);
      else next.delete(message.id);
      return next;
    });
    if (opening && comments[message.id] === undefined) void loadComments(message.id);
  };

  const toggleLike = async (message: BoardMessage) => {
    if (!user) return onNeedAuth();
    if (!canLikeWithoutBinding) return toast('请先绑定游戏角色后再点赞', 'error');
    if (actionBusy[message.id]) return;
    setActionBusy(previous => ({ ...previous, [message.id]: true }));
    try {
      const result = await api<{ liked: boolean; likeCount: number }>(`/api/board/messages/${message.id}/like`, { method: 'POST' });
      setMessages(previous => previous?.map(item => item.id === message.id ? { ...item, likedByMe: result.liked, likeCount: result.likeCount } : item) || previous);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '点赞失败', 'error');
    } finally {
      setActionBusy(previous => ({ ...previous, [message.id]: false }));
    }
  };

  const togglePin = async (message: BoardMessage) => {
    if (!permissions.isSuperadmin) return toast('只有超级管理员可以置顶留言', 'error');
    setActionBusy(previous => ({ ...previous, [message.id]: true }));
    try {
      await api(`/api/board/messages/${message.id}/pin`, { method: 'PATCH', body: JSON.stringify({ pinned: !message.pinned }) });
      await load();
      toast(message.pinned ? '已取消置顶' : '留言已置顶', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '置顶操作失败', 'error');
    } finally {
      setActionBusy(previous => ({ ...previous, [message.id]: false }));
    }
  };

  const remove = async (message: BoardMessage) => {
    const ok = await confirm({ title: '删除留言', body: '确认删除这条留言？评论和点赞也会一并隐藏。', danger: true, confirmText: '删除' });
    if (!ok) return;
    try {
      await api('/api/board/messages/' + message.id, { method: 'DELETE' });
      await load();
      toast('留言已删除', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '删除失败', 'error');
    }
  };

  const submitComment = async (event: FormEvent, message: BoardMessage) => {
    event.preventDefault();
    if (!user) return onNeedAuth();
    if (!canCommentWithoutBinding) return toast('请先绑定游戏角色后再评论', 'error');
    const text = String(commentDrafts[message.id] || '').trim();
    if (!text) return;
    setCommentBusy(previous => ({ ...previous, [message.id]: true }));
    try {
      const result = await api<{ comment: BoardComment | null; commentCount: number; moderation: { status: 'approved' | 'pending'; reason: string } }>(`/api/board/messages/${message.id}/comments`, { method: 'POST', body: JSON.stringify({ content: text }) });
      setCommentDrafts(previous => ({ ...previous, [message.id]: '' }));
      if (result.comment) {
        setComments(previous => ({ ...previous, [message.id]: [...(previous[message.id] || []), result.comment!] }));
      }
      setMessages(previous => previous?.map(item => item.id === message.id ? { ...item, commentCount: result.commentCount } : item) || previous);
      if (result.moderation?.status === 'pending') toast('评论已提交，等待管理员复核', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '评论失败', 'error');
    } finally {
      setCommentBusy(previous => ({ ...previous, [message.id]: false }));
    }
  };

  const removeComment = async (message: BoardMessage, comment: BoardComment) => {
    const ok = await confirm({ title: '删除评论', body: '确认删除这条评论？', danger: true, confirmText: '删除' });
    if (!ok) return;
    try {
      const result = await api<{ commentCount: number }>('/api/board/comments/' + comment.id, { method: 'DELETE' });
      setComments(previous => ({ ...previous, [message.id]: (previous[message.id] || []).filter(item => item.id !== comment.id) }));
      setMessages(previous => previous?.map(item => item.id === message.id ? { ...item, commentCount: result.commentCount } : item) || previous);
      toast('评论已删除', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '删除评论失败', 'error');
    }
  };

  return (
    <>
      <PageHeader title="留言板" description="分享想法、反馈和建议，也可以回复其他成员。参与留言、评论和点赞需要登录。" />

      <Panel className="board-editor-panel">
        {!user ? (
          <div className="board-editor-notice">
            <span className="board-avatar"><LogIn size={19} /></span>
            <div><strong>登录后参与留言</strong><span>游客可以浏览，登录并绑定游戏角色后可以发布留言。</span></div>
            <Button variant="primary" onClick={onNeedAuth}><LogIn size={16} /> 登录 / 注册</Button>
          </div>
        ) : !canPostWithoutBinding ? (
          <div className="board-editor-notice">
            <span className="board-avatar"><Link2 size={19} /></span>
            <div><strong>尚未绑定游戏角色</strong><span>当前管理员要求绑定游戏角色后才能留言。</span></div>
            <Button variant="primary" onClick={() => { window.location.hash = '/profile'; }}><Link2 size={16} /> 去绑定</Button>
          </div>
        ) : (
          <form className="board-comment-editor" onSubmit={submit}>
            <BoardAvatar name={user.username} avatar={user.avatar} className="board-editor-avatar" />
            <div className="board-comment-editor-main">
              <Textarea value={content} onChange={event => setContent(event.target.value)} rows={3} maxLength={500} placeholder="写下想对服务器或其他成员说的话…" required />
              <div className="board-comment-editor-actions">
                <span>{content.length}/500 · 每 10 秒最多发布一条</span>
                <Button variant="primary" type="submit" loading={busy}><Send size={15} /> 发布留言</Button>
              </div>
            </div>
          </form>
        )}
      </Panel>

      <Panel title={'全部留言 · ' + (messages?.length || 0)} eyebrow="COMMENTS" className="board-feed-panel top-gap">
        {!messages ? <LoadingBlock /> : messages.length === 0 ? <EmptyBlock title="还没有留言" description="成为第一个留言的人。" /> : (
          <div className="board-list">
            {messages.map(message => {
              const canDelete = Boolean(user && (message.userId === user.id || permissions.isStaff));
              const commentsOpen = openComments.has(message.id);
              const messageComments = comments[message.id];
              const likeBusy = Boolean(actionBusy[message.id]);
              return (
                <article className={'board-message' + (message.pinned ? ' pinned' : '')} key={message.id}>
                  <BoardAvatar name={message.username} avatar={message.avatar} className="board-message-avatar" />
                  <div className="board-message-main">
                    <header className="board-message-header">
                      <div className="board-author-line">
                        <strong>{message.displayName}</strong>
                        <span className="board-username">@{message.username}</span>
                        {message.pinned && <Badge tone="gold"><Pin size={11} /> 置顶</Badge>}
                        {permissions.isStaff && message.userId !== user?.id && <span className="board-admin-note">管理员可管理</span>}
                      </div>
                      <time>{formatTime(message.createdAt)}</time>
                    </header>

                    <div className="board-message-content">{message.content}</div>

                    <div className="board-message-actions">
                      <button type="button" className={'board-action-button liked' + (message.likedByMe ? ' active' : '')} disabled={likeBusy} onClick={() => void toggleLike(message)} aria-pressed={message.likedByMe} aria-label={(message.likedByMe ? '取消点赞' : '点赞') + '，当前 ' + message.likeCount + ' 个赞'}>
                        <Heart size={17} fill={message.likedByMe ? 'currentColor' : 'none'} />
                        <span>{message.likedByMe ? '已赞' : '点赞'}</span>
                        <em>{message.likeCount}</em>
                      </button>
                      <button type="button" className={'board-action-button' + (commentsOpen ? ' active' : '')} onClick={() => toggleComments(message)} aria-expanded={commentsOpen} aria-label={(commentsOpen ? '收起评论，共 ' : '查看评论，共 ') + message.commentCount + ' 条'}>
                        <MessageSquare size={17} />
                        <span>{commentsOpen ? '收起评论' : '评论'}</span>
                        <em>{message.commentCount}</em>
                      </button>
                      {permissions.isSuperadmin && (
                        <button type="button" className="board-action-button admin-action" disabled={likeBusy} aria-label={message.pinned ? "取消置顶" : "置顶留言"} onClick={() => void togglePin(message)}>
                          {message.pinned ? <PinOff size={16} /> : <Pin size={16} />}
                          <span>{message.pinned ? '取消置顶' : '置顶'}</span>
                        </button>
                      )}
                      {canDelete && (
                        <button type="button" className="board-action-button admin-action danger" onClick={() => void remove(message)} aria-label="删除留言">
                          <Trash2 size={16} /><span>删除</span>
                        </button>
                      )}
                    </div>

                    {commentsOpen && (
                      <section className="board-comments">
                        <header className="board-comments-header">
                          <strong>{message.commentCount} 条评论</strong>
                          <button type="button" onClick={() => toggleComments(message)}>收起</button>
                        </header>

                        {messageComments === undefined || messageComments === null ? <LoadingBlock label="正在加载评论" /> : messageComments.length === 0 ? (
                          <div className="board-comments-empty">还没有评论，来补充第一条吧。</div>
                        ) : (
                          <div className="board-comment-list">
                            {messageComments.map(comment => {
                              const canDeleteComment = Boolean(user && (comment.userId === user.id || permissions.isStaff));
                              return (
                                <article className="board-comment" key={comment.id}>
                                  <BoardAvatar name={comment.username} avatar={comment.avatar} className="board-comment-avatar" />
                                  <div className="board-comment-main">
                                    <header className="board-comment-header">
                                      <strong>{comment.displayName}</strong>
                                      <span>@{comment.username}</span>
                                      <time>{formatTime(comment.createdAt)}</time>
                                    </header>
                                    <p>{comment.content}</p>
                                    {canDeleteComment && (
                                      <footer className="board-comment-actions">
                                        <button type="button" onClick={() => void removeComment(message, comment)}><Trash2 size={13} />删除</button>
                                      </footer>
                                    )}
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        )}

                        {!user ? (
                          <div className="board-comment-login"><Button variant="ghost" onClick={onNeedAuth}><LogIn size={14} /> 登录后评论</Button></div>
                        ) : !canCommentWithoutBinding ? (
                          <div className="board-comment-login"><Button variant="ghost" onClick={() => { window.location.hash = '/profile'; }}><Link2 size={14} /> 绑定角色后评论</Button></div>
                        ) : (
                          <div className="board-comment-compose">
                            <BoardAvatar name={user.username} avatar={user.avatar} className="board-comment-avatar" />
                            <form className="board-comment-form" onSubmit={event => void submitComment(event, message)}>
                              <Textarea rows={2} maxLength={300} value={commentDrafts[message.id] || ''} onChange={event => setCommentDrafts(previous => ({ ...previous, [message.id]: event.target.value }))} placeholder="回复这条留言…" required />
                              <div className="board-comment-form-actions">
                                <span>{String(commentDrafts[message.id] || '').length}/300 · 每 5 秒最多一条</span>
                                <Button variant="primary" type="submit" loading={Boolean(commentBusy[message.id])}><Send size={14} /> 发表评论</Button>
                              </div>
                            </form>
                          </div>
                        )}
                      </section>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </Panel>
    </>
  );
}





